package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import dev.lingvo.shared.StoreConnectionDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.checkStoreConnection
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.copySafely
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun StorePlayWizard(
    connection: StoreConnectionDto,
    orgSlug: String,
    runner: StoreActionRunner,
    busy: Boolean,
    setBusy: (Boolean) -> Unit,
    clearError: () -> Unit,
    onError: (Throwable) -> Boolean,
    onBind: (String) -> Unit,
) {
    var ref by remember(connection.id) { mutableStateOf("") }
    var verifiedRef by remember(connection.id) { mutableStateOf<String?>(null) }
    var copied by remember(connection.id) { mutableStateOf(false) }
    var copyError by remember(connection.id) { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val email = connection.identity["serviceAccount"].orEmpty()
    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
        LMuted(t("app.projects.slug.store_play_wizard.service_account_email", "Service account email"))
        Div(attrs = { style { property("overflow-wrap", "anywhere"); property("max-width", "100%") } }) { Text(email) }
        LButton(
            if (copied) t("app.projects.slug.store_play_wizard.email_copied", "Email copied") else t("app.projects.slug.store_play_wizard.copy_email", "Copy email"),
            onClick = { scope.launch {
                try { copySafely(email); copied = true; copyError = false }
                catch (cancelled: CancellationException) { throw cancelled }
                catch (_: Throwable) { copyError = true }
            } }, variant = LButtonVariant.SECONDARY, enabled = email.isNotBlank(),
        )
        if (copyError) LAlert(t("app.projects.slug.store_play_wizard.couldnt_copy_the_email", "Couldn't copy the email"),
            t("app.projects.slug.store_play_wizard.select_and_copy_the_email_above", "Select and copy the email above, then paste it into Play Console."), variant = LAlertVariant.WARNING, live = true)
        LMuted(t("app.projects.slug.store_play_wizard.in_play_console_users_and_permissions", "In Play Console → Users and permissions → Invite new users, paste this email. Give it access to your app and grant Manage store presence."))
        LMuted(t("app.projects.slug.store_play_wizard.to_write_release_notes_too_grant", "To write release notes too, grant Release to production. You don't need to link a Cloud project or download a key."))
        LTextField(ref, { ref = it; verifiedRef = null; clearError() }, modifier = Modifier.fillMaxWidth(),
            label = t("app.projects.slug.store_play_wizard.package_name", "Package name"), placeholder = t("app.projects.slug.store_play_wizard.com_example_app", "com.example.app"), enabled = !busy)
        LMuted(t("app.projects.slug.store_play_wizard.the_check_opens_and_discards_an", "The check opens and discards an edit. It does not publish a change."))
        LButton(t("app.projects.slug.store_play_wizard.check_package_access", "Check package access"), onClick = {
            clearError()
            runner.run(setBusy, tr("app.projects.slug.store_play_wizard.couldnt_reach_this_package_check_the", "Couldn't reach this package. Check the invitation and try again."),
                transport = { ApiClient.checkStoreConnection(orgSlug, connection.id, ref.trim()) }, onError = onError,
            ) { verifiedRef = it.storeAppRef }
        }, variant = LButtonVariant.SECONDARY, enabled = ref.isNotBlank() && !busy, loading = busy)
        if (verifiedRef == ref.trim() && verifiedRef != null) {
            LAlert(t("app.projects.slug.store_play_wizard.package_access_verified", "Package access verified"),
                t("app.projects.slug.store_play_wizard.play_accepted_the_invitation_and_package", "Play accepted the invitation and package. Connect the app, then import its listing."), variant = LAlertVariant.SUCCESS, live = true)
            LButton(t("app.projects.slug.store_play_wizard.connect_app", "Connect app"), onClick = { onBind(ref.trim()) }, enabled = !busy, loading = busy)
        }
    }
}
